import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRight, ArrowLeft, Shield, Clock, Check } from 'lucide-react';
import TrialSignupFlow from '@/shared/components/TrialSignupFlow';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import BrandedLoader from '@/shared/components/BrandedLoader';
import { ExamScheduler } from '@/features/scheduler/ExamScheduler';
import RetakeStep from '@/features/onboarding/RetakeStep';
import {
  buildRetakeReport,
  EMPTY_RETAKE_ANSWERS,
  type RetakeAnswers,
  type RetakeReport,
} from '@/features/onboarding/retakeReport';
import { profileService } from '@/services/ProfileService';
import { TestType } from '@/models/SharedTypes';
import {
  PreferredStudyTime,
  type ExamScheduleEntry,
  type ExamStatus,
} from '@/models/UserTypes';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { useRedirectAuthenticatedUser } from '@/hooks/useRedirectAuthenticatedUser';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { readDiagnosticResult, DiagnosticExamType } from '@/utils/diagnosticMemory';
import { loadPendingSocialSignup } from '@/utils/pendingSocialSignup';
import { trackAimDone } from '@/utils/signupRoad';
import { logger } from '@/utils/logger';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { callWithTimeout } from '@/utils/callWithTimeout';
import { computeLeadId, leadIdFromPassPlanToken, loadLeadRef } from '@/utils/leadRef';
import { wasMintedFromPassPlan, clearPassPlanMint, redeemPassPlanAuth } from '@/utils/passPlanAuth';
import { readPassPlanUrlToken, clearPassPlanUrlToken } from '@/utils/passPlanAccess';
import { isLicensingPathStepEnabled, isPublicDiagnosticSixTurnsEnabled, isTryFirstQuestionEnabled, isPassPlanTrialAtMintEnabled } from '@/config/featureFlags';
import AimScreen, { type AimAnswers } from '@/features/onboarding/AimScreen';
import LicensingStep from '@/features/licensing/LicensingStep';
import { useLicensingPath } from '@/features/licensing/useLicensingPath';
import { isLicensingComplete, licensingPayload } from '@/features/licensing/flow';
import { foldExamSchedule } from '@/features/licensing/examStatus';
import { isAvailable, type LicensingAnswers } from '@/features/licensing/types';
import { allSchools, filterSchools, findSchoolByCode } from '@/features/programs/programs';
import { contextProfileFill, learnerContext } from '@/utils/learnerContext';
import { skipExamScheduledCelebrationFromOnboarding } from '@/shared/components/CelebrationOverlay';
import { DEFAULT_STUDY_GOAL, EXAM_TO_TEST_TYPE, readUserDoc, saveAimProfile, warmTrialPlan } from '@/features/onboarding/aimProfile';

/**
 * The unified, account-LAST web onboarding (spec:
 * docs/product/unified-onboarding-account-last-spec.md).
 *
 * P2 inserts the three REQUIRED personalize screens BEFORE account creation. The
 * full sequence:
 *   - Screen A "plan"    : momentum header carrying the diagnostic score + exam confirm.
 *   - Screen B "exam"    : exam date/status via <ExamScheduler/> (reused, persist=false).
 *   - Screen C "goal"    : preferred study time (the reused study-goal step field).
 *   - Screen D "name"    : first name (personalizes the app + emails).
 *   - Screen D2 "retake" : OPTIONAL prior-attempt capture. The only skippable
 *     screen: "No" is preselected and Continue is never gated, so it costs a
 *     first-timer one tap. A "Yes" rides into createTrialUser as `retakeReport`,
 *     which the backend hardens into isRetaker + userSegment=retaker at account
 *     creation — no second write, and nothing to lose if the profile patch fails.
 *   - Screen E "account" : the EXISTING <TrialSignupFlow/> — account is the LAST step.
 * Screens A–D are pre-account: their answers live in LOCAL STATE (no auth yet).
 * Each requires its answer before "Continue" (the locked "REQUIRED" decision).
 *
 * The data thread (money-path — see the spec's "The data thread"):
 *   - name + a *scheduled* examDate are threaded into createTrialUser via
 *     TrialSignupFlow's additive `personalization` prop (createTrialUser already
 *     accepts name/examDate/examType). No auth/provisioning control flow changes.
 *   - studyGoal + preferredStudyTime + the full ExamScheduleEntry are NOT accepted
 *     by createTrialUser, so they are written by updateUserProfile (same callable +
 *     shape Onboarding.tsx uses) in a BEST-EFFORT call right after onSuccess (the
 *     user is authed by then). It sets onboardingCompleted:true so the Dashboard
 *     soft-onboarding modal doesn't re-prompt. If it fails we log and still route
 *     to the dashboard — a successful signup is never blocked by the profile write.
 *
 * Money-path safety (see the spec's "Auth / trial timing — MUST preserve"):
 *   - TrialSignupFlow owns ALL auth/provisioning. We reuse it as-is; the ONLY new
 *     surface is the additive `personalization` prop (payload-only).
 *   - REDIRECT-COMPLETION SAFETY: TrialSignupFlow's mount-time
 *     `completeSocialRedirect` effect finishes in-app-browser social signups. So
 *     when a social redirect is returning (`loadPendingSocialSignup()` present),
 *     we START on Screen E so TrialSignupFlow mounts immediately and completes it —
 *     the personalize screens are skipped in that case (acceptable: those users
 *     personalize via the Dashboard modal, which stays available because we only
 *     write onboardingCompleted for visitors who actually walked the screens).
 *   - The exam confirm reaches TrialSignupFlow via the `?exam=` URL param it reads
 *     on mount — we `replaceState` it before advancing to 'account', and only
 *     render TrialSignupFlow on that screen so it mounts AFTER the param is set.
 */

type OnboardingScreen =
  | 'plan'
  | 'licensing'
  | 'exam'
  | 'examDate'
  | 'passGoal'
  | 'goal'
  | 'name'
  | 'retake'
  | 'account';

/** ?src= value that routes onboarding to the trimmed trial doorway (see below). */
const DOORWAY_SRC = 'trial_doorway';
/** Where the six turns send a finisher; a signed-in one gets the aim screen (meta #321 P2-3). */
const PUBLIC_DIAGNOSTIC_SRC = 'public_diagnostic';

/**
 * Full progress rail, in order. The licensing step (#411) sits right after the
 * plan, because everything after it can change with the answer: the exam-date
 * screen is pre-filled (and sometimes skipped) from where the learner says they
 * are with their board. Flag OFF → the exact array this file has always had.
 *
 * The doorway (`?src=trial_doorway`, meta issue #225): the visitor already has
 * an account + trial from the diagnostic capture, so there is no account screen
 * to walk to — exactly six taps for a dated non-retaker: exam confirm, exam
 * date (with a real "no date yet"), retake, goal, state/road-stage, study
 * time. The optional school lookup rides the study-time screen as an inline
 * "Add my program" link (see the `goal` screen JSX) rather than its own tap,
 * so it never adds a seventh screen to this array. `account` is NOT listed
 * here — it is a fallback the doorway's finish reaches only if the visitor
 * turns out to be unauthenticated (see `finishOrCreateAccount`), so it never
 * counts toward the six.
 */
function buildScreenOrder(licensingEnabled: boolean, isDoorway: boolean): readonly OnboardingScreen[] {
  if (isDoorway) return ['plan', 'examDate', 'retake', 'passGoal', 'licensing', 'goal'];
  return licensingEnabled
    ? ['plan', 'licensing', 'exam', 'goal', 'name', 'retake', 'account']
    : ['plan', 'exam', 'goal', 'name', 'retake', 'account'];
}

/**
 * The GA4 `step` value per screen. The licensing step reports the name the
 * funnel + the plan agreed on (`licensing_pathway`), not its internal id.
 */
const STEP_EVENT_NAME: Partial<Record<OnboardingScreen, string>> = {
  licensing: 'licensing_pathway',
  examDate: 'exam_date',
  passGoal: 'study_goal',
};

/** Preferred study time → the hour-of-day the backend schedules reminders around. */
const STUDY_HOUR_BY_TIME: Record<PreferredStudyTime, number> = {
  [PreferredStudyTime.MORNING]: 8,
  [PreferredStudyTime.AFTERNOON]: 13,
  [PreferredStudyTime.EVENING]: 19,
  [PreferredStudyTime.NIGHT]: 21,
};

const EXAM_CHOICES: readonly DiagnosticExamType[] = ['LBSW', 'LMSW', 'LCSW'];

const EXAM_PARAM_MAP: Record<string, DiagnosticExamType> = {
  lbsw: 'LBSW',
  lmsw: 'LMSW',
  lcsw: 'LCSW',
};


const STUDY_TIME_OPTIONS: { value: PreferredStudyTime; label: string; time: string }[] = [
  { value: PreferredStudyTime.MORNING, label: 'Morning', time: '6am - 12pm' },
  { value: PreferredStudyTime.AFTERNOON, label: 'Afternoon', time: '12pm - 5pm' },
  { value: PreferredStudyTime.EVENING, label: 'Evening', time: '5pm - 9pm' },
  { value: PreferredStudyTime.NIGHT, label: 'Night', time: '9pm - 12am' },
];

const VALID_STATUSES: readonly ExamStatus[] = ['scheduled', 'approved', 'waiting', 'accruing', 'exploring'];

/**
 * Whether a working ExamScheduler entry has enough to advance — mirrors the
 * scheduler's own `isComplete`: 'scheduled' needs a date, 'approved'/'waiting'
 * need a target window, 'accruing'/'exploring' are always complete. Keeps the
 * REQUIRED gate meaningful so a status pick alone can't skip its follow-up field.
 */
function isScheduleComplete(entry: ExamScheduleEntry | null): boolean {
  if (!entry) return false;
  switch (entry.status) {
    case 'scheduled':
      return !!entry.date;
    case 'approved':
    case 'waiting':
      return !!entry.targetWindow;
    default:
      return true;
  }
}

/** The exam the visitor already told us: ?exam= param first, else the diagnostic's pick. */
function knownExam(): DiagnosticExamType | null {
  const param = new URLSearchParams(window.location.search).get('exam');
  if (param && EXAM_PARAM_MAP[param.toLowerCase()]) {
    return EXAM_PARAM_MAP[param.toLowerCase()];
  }
  return readDiagnosticResult()?.examType ?? null;
}

/** Exam to default the confirm to: the known exam, else the door's, else LCSW. */
function initialExam(): DiagnosticExamType {
  return knownExam() ?? learnerContext().exam ?? 'LCSW';
}

/** The /try handoff rides the "when are you testing?" answer in on ?examStatus=. */
function initialExamStatus(): ExamStatus | null {
  const raw = new URLSearchParams(window.location.search).get('examStatus');
  return raw && (VALID_STATUSES as readonly string[]).includes(raw) ? (raw as ExamStatus) : null;
}

/** `?src=` as the diagnostic report's doorway CTA sends it (trial_doorway). */
function initialSrc(): string | null {
  return new URLSearchParams(window.location.search).get('src');
}

/**
 * Start on Screen E ('account') ONLY when a social redirect is returning, so
 * TrialSignupFlow mounts immediately and its completeSocialRedirect effect can
 * finish the signup. Otherwise start on Screen A ('plan').
 */
function initialScreen(): OnboardingScreen {
  return loadPendingSocialSignup() ? 'account' : 'plan';
}

/**
 * Counts up from 0 to `target` once on mount (easeOutCubic) — the reveal→plan
 * payoff: the readiness score the visitor earned lands with momentum before the
 * plan builds in beneath it. Honors prefers-reduced-motion (renders the final
 * value instantly, no animation).
 */
const CountUp: React.FC<{ target: number; durationMs?: number }> = ({ target, durationMs = 850 }) => {
  const prefersReduced =
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [value, setValue] = useState(prefersReduced ? target : 0);
  useEffect(() => {
    if (prefersReduced) return;
    let raf = 0;
    let startTs: number | null = null;
    const step = (ts: number) => {
      if (startTs === null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, prefersReduced]);
  return <span className="tabular-nums">{value}</span>;
};

/** Pins a step's action to the viewport bottom so it is on screen on an iPhone SE without scrolling.
 *  Solid surface + ledge rule so the content visibly scrolls under a distinct bar. */
const StickyAction: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="sticky bottom-0 z-10 -mx-4 border-t-2 border-[rgb(var(--color-ledge))] bg-white px-4 pb-4 pt-3">
    {children}
  </div>
);

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<OnboardingScreen>(initialScreen);
  // Optional prior-attempt answers. Default is "not a retaker", so a visitor who
  // taps straight through sends nothing.
  const [retakeAnswers, setRetakeAnswers] = useState<RetakeAnswers>(EMPTY_RETAKE_ANSWERS);
  const [exam, setExam] = useState<DiagnosticExamType>(initialExam);
  const [error, setError] = useState<string>('');
  // P4 momentum: after a successful signup we write the gathered profile then
  // route to the dashboard. Show a branded "building your plan" state across that
  // gap so the handoff reads as intentional, not a flash of the frozen form.
  const [finalizing, setFinalizing] = useState(false);

  // Personalize answers, gathered pre-account into LOCAL STATE (no auth yet).
  const [scheduleEntry, setScheduleEntry] = useState<ExamScheduleEntry | null>(null);
  // The jurisdiction step's answers (#411). Empty object = never answered, which
  // is what a flag-off build and a social-redirect start both send.
  const [licensingAnswers, setLicensingAnswers] = useState<LicensingAnswers>(() => {
    const { jurisdiction } = learnerContext();
    return jurisdiction ? { jurisdiction } : {};
  });
  const [preferredStudyTime, setPreferredStudyTime] = useState<PreferredStudyTime | null>(null);
  const [name, setName] = useState<string>('');
  const [urlExamStatus] = useState<ExamStatus | null>(initialExamStatus);

  // The doorway (#225): read once, same build-time-URL semantics as urlExamStatus.
  const [src] = useState<string | null>(initialSrc);
  const isDoorway = src === DOORWAY_SRC;
  // The doorway comes straight from the diagnostic: re-asking an exam it already knows lost visitors.
  const [askExam] = useState(() => !isDoorway || knownExam() == null);
  // Doorway-only answers. `doorwayNoDate` backs the "I don't have a date yet"
  // choice — distinct from `scheduleEntry: null` (unanswered) because it must
  // still satisfy the REQUIRED gate without ever inventing a fake date.
  const [doorwayNoDate, setDoorwayNoDate] = useState(false);
  const [passGoalOverride, setPassGoalOverride] = useState<'first' | 'retake' | null>(null);
  const [schoolCode, setSchoolCode] = useState<string | undefined>(() => learnerContext().schoolCode);
  const [schoolQuery, setSchoolQuery] = useState(() => (schoolCode && findSchoolByCode(schoolCode)?.name) || '');
  // The school lookup is an inline "Add my program" link on the study-time
  // screen, not its own tap — collapsed until asked for.
  const [schoolExpanded, setSchoolExpanded] = useState(() => !!schoolCode);
  // Doorway visitors are normally already authenticated (minted at diagnostic
  // capture). If one somehow reaches finish without a session — a stale link,
  // a redeem that silently failed — this ref tells handleTrialSuccess to run
  // the SAME doorway finish after account creation instead of the legacy
  // post-account shortcut.
  const doorwayNeedsAccountRef = useRef(false);
  const { currentUser, userData, hasProfile, isLoading: authLoading, isAuthLoading, refreshUserData } = useEnhancedAuth();
  const [aimSaving, setAimSaving] = useState(false);

  // True once the visitor has actually walked the personalize screens (name →
  // account). A social-redirect start jumps straight to 'account' and leaves this
  // false, so the post-success write is skipped and the Dashboard modal still
  // prompts them — the documented fallback. A ref so onSuccess reads the latest
  // value without re-subscribing.
  const personalizedRef = useRef(false);

  // Carry the diagnostic score into the momentum header. Read once on mount:
  // localStorage doesn't change under us mid-flow.
  const [readiness] = useState<number | null>(() => {
    const diagnostic = readDiagnosticResult();
    return typeof diagnostic?.readiness === 'number' ? diagnostic.readiness : null;
  });

  // A Pass Plan redeem signs the visitor in at CAPTURE, so by the time they click
  // "Start free for 7 days" they are authenticated with no trial. Read once on
  // mount: the flag is cleared on success, and that must not re-arm the sweep
  // mid-navigation.
  const [mintedContinuer, setMintedContinuer] = useState(wasMintedFromPassPlan);
  // The backend granted the trial at mint, so this walk ends on /dashboard, not the trial grant.
  const trialAtMint = isPassPlanTrialAtMintEnabled() && mintedContinuer && userData?.isTrialActive === true;

  // The Pass Plan email's trial CTA lands here with the download token on `?t=`,
  // so redeem it on this page too. Held until settled so the redirect guard
  // cannot sweep the visitor the instant the redeem signs them in.
  const [passPlanToken] = useState(() => readPassPlanUrlToken('onboarding'));
  const [redeeming, setRedeeming] = useState(!!passPlanToken);
  const redeemStartedRef = useRef(false);
  useEffect(() => {
    if (!passPlanToken || redeemStartedRef.current) return;
    redeemStartedRef.current = true;
    clearPassPlanUrlToken('onboarding');
    const url = new URL(window.location.href);
    if (url.searchParams.has('t')) {
      url.searchParams.delete('t');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
    void redeemPassPlanAuth(passPlanToken, 'pass_plan_link').then(() => {
      setMintedContinuer(wasMintedFromPassPlan());
      setRedeeming(false);
    });
  }, [passPlanToken]);

  // Never strand an authenticated user on this account-bearing page — same guard
  // TrialSignupPage uses. Cedes while a social redirect is mid-flight (bridge
  // present) so TrialSignupFlow owns completion, and for a Pass Plan mint, who
  // is signed in but still owed the personalize walk and the trial grant.
  // A doorway visitor is ALSO already authenticated (minted at diagnostic
  // capture) but has not walked the personalize screens yet — same reason
  // mintedContinuer is skipped above.
  // On the account screen the only way to become authenticated is TrialSignupFlow's
  // own signup, which routes on success. Sweeping here raced provisioning and
  // stranded a rejected signup (e.g. a mistyped email domain) on /signin.
  // Any profile with a targetExam (aim, pricing, iOS, other signups) never sees aim:
  // the redirect below sends her to /dashboard like any returning learner.
  const aimEligible = src === PUBLIC_DIAGNOSTIC_SRC && isPublicDiagnosticSixTurnsEnabled();
  // A visitor who signs in on this page (account screen, social return, finalize)
  // finishes the flow she is in; aim and its loader are for arriving signed in.
  const aimSuppressed = screen === 'account' || finalizing || personalizedRef.current;
  const aimSignedIn = aimEligible && !aimSuppressed && !!currentUser;
  const showAim = aimSignedIn && hasProfile && !userData?.profile?.targetExam;

  const leaving = useRedirectAuthenticatedUser({
    skip: mintedContinuer || isDoorway || redeeming || screen === 'account' || showAim,
  });
  // A signed-in visitor swept off this page never entered onboarding. Aim is shown only to an
  // account with no targetExam (a fresh six-turn mint), so it is her onboarding entry and counts.
  const countable = !isAuthLoading && !leaving;

  // "started = enter onboarding" (spec). Keep the event + sticky variant tag so
  // ab.py still attributes landing → signup.
  const startedLoggedRef = useRef(false);
  useEffect(() => {
    if (!countable || startedLoggedRef.current) return;
    startedLoggedRef.current = true;
    logAnalyticsEvent(ANALYTICS_EVENTS.TRIAL_SIGNUP_STARTED, {
      signup_method: 'onboarding',
      timestamp: new Date().toISOString(),
    });
    if (isDoorway) {
      logAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_DOORWAY_VIEWED, {
        timestamp: new Date().toISOString(),
      });
    }
  }, [countable, isDoorway]);

  // Per-screen progression (spec P3): fire onboarding_step_viewed on every screen
  // so the funnel no longer goes dark between capture and trial — we can see WHERE
  // people drop inside onboarding. Keyed on `screen` ONLY (exam via a ref, so
  // toggling exam type on the plan screen doesn't re-fire the same step); also
  // fires once on the redirect-safe direct-to-account start.
  const examTypeRef = useRef(exam);
  examTypeRef.current = exam;
  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [screen]);
  const viewedScreenRef = useRef<OnboardingScreen | null>(null);
  useEffect(() => {
    // Aim renders in place of the rail while `screen` still reads 'plan'.
    if (!countable || showAim || viewedScreenRef.current === screen) return;
    viewedScreenRef.current = screen;
    logAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_VIEWED, {
      step: STEP_EVENT_NAME[screen] ?? screen,
      step_index: screenOrder.indexOf(screen),
      exam_type: examTypeRef.current,
      timestamp: new Date().toISOString(),
    });
  }, [screen, countable, showAim]);

  const track = EXAM_TO_TEST_TYPE[exam];

  // Build-time flag, read once: the rail's length must not change under the
  // visitor mid-flow.
  const [licensingEnabled] = useState(isLicensingPathStepEnabled);
  // The doorway always shows the state/road-stage step regardless of the flag
  // (it's optional there, never gated), per the doorway spec.
  const licensingVisible = licensingEnabled || isDoorway;
  const screenOrder = React.useMemo(
    () => {
      const order = buildScreenOrder(licensingEnabled, isDoorway);
      return trialAtMint ? order.filter((s) => s !== 'account') : order;
    },
    [licensingEnabled, isDoorway, trialAtMint]
  );

  // Re-queried on EVERY licensing answer that can change the response, which is
  // the point of the step: the next question and the callout both move with it.
  const licensing = useLicensingPath(
    licensingVisible ? licensingAnswers.jurisdiction : undefined,
    licensingAnswers.licenseRung,
    licensingAnswers.processStage
  );
  const foldedSchedule = foldExamSchedule(licensingAnswers);

  // The lead handle for the P3b mirror: the sha256(email) hash the diagnostic
  // stashed at capture (NEVER the raw email). Absent for a visitor who reached
  // /onboarding without capturing (e.g. a direct link) — the mirror then no-ops.
  // Email-CTA returners carry the hash in the Pass Plan token's `id` claim, or
  // ?email= on older emails. Held in a ref (async resolve).
  const leadIdRef = useRef<string | null>(loadLeadRef() ?? leadIdFromPassPlanToken(passPlanToken));
  useEffect(() => {
    if (leadIdRef.current) return;
    const emailParam = new URLSearchParams(window.location.search).get('email');
    if (!emailParam) return;
    computeLeadId(emailParam)
      .then((id) => {
        leadIdRef.current = id;
      })
      .catch((err) => logger.warn('OnboardingPage', 'leadId derive failed (non-fatal)', { err }));
  }, []);

  // Latest gathered personalize answers, via a ref, so the fire-and-forget mirror
  // reads current values without stale closures / re-created callbacks.
  const gatheredRef = useRef({ name, scheduleEntry, preferredStudyTime });
  gatheredRef.current = { name, scheduleEntry, preferredStudyTime };

  // Best-effort mirror of the gathered personalize answers into the lead doc,
  // addressed by hash (spec P3b): a dropper stays resumable + the drip can
  // reference in-progress onboarding. Fire-and-forget — never blocks navigation,
  // never surfaces an error, no-ops without a lead handle. Merges are idempotent,
  // so calling on each advance progressively enriches the same doc.
  const mirrorToLead = useCallback(() => {
    const leadId = leadIdRef.current;
    if (!leadId) return;
    const g = gatheredRef.current;
    const examDate =
      g.scheduleEntry?.status === 'scheduled' && g.scheduleEntry.date ? g.scheduleEntry.date : undefined;
    const payload = {
      leadId,
      ...(g.name.trim() ? { name: g.name.trim() } : {}),
      ...(examDate ? { examDate } : {}),
      studyGoal: DEFAULT_STUDY_GOAL,
    };
    callWithTimeout(() => httpsCallable(functions, 'updateLeadPersonalization')(payload), {
      timeoutMs: 8000,
      retries: 0,
    }).catch((err) => logger.warn('OnboardingPage', 'lead mirror failed (non-fatal)', { err }));
  }, []);

  const goTo = useCallback(
    (next: OnboardingScreen) => {
      // Hand the exam to TrialSignupFlow via the ?exam= param it reads on mount.
      // Set it BEFORE switching to 'account'; TrialSignupFlow only mounts there,
      // so it reads the freshly-written param.
      if (next === 'account') {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('exam', exam.toLowerCase());
          window.history.replaceState(window.history.state, '', url.toString());
        } catch {
          /* replaceState best-effort; TrialSignupFlow falls back to diagnostic memory */
        }
        personalizedRef.current = true;
      }
      // Doorway steps have no separate completeXxx callback (see completeLicensing
      // for the one legacy screen that needs one), so goTo is where each doorway
      // step reports done — mirrors the shape ONBOARDING_STEP_VIEWED already uses.
      if (isDoorway) {
        logAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, {
          step: STEP_EVENT_NAME[screen] ?? screen,
          step_index: screenOrder.indexOf(screen),
          exam_type: exam,
          timestamp: new Date().toISOString(),
        });
      }
      // Mirror the answers gathered SO FAR into the lead doc (best-effort).
      mirrorToLead();
      setError('');
      setScreen(next);
    },
    [exam, mirrorToLead, isDoorway, screen, screenOrder]
  );

  const goBack = useCallback(() => {
    setError('');
    setScreen((current) => {
      const idx = screenOrder.indexOf(current);
      return idx > 0 ? screenOrder[idx - 1] : current;
    });
  }, [screenOrder]);

  /**
   * Leave the jurisdiction step. Fires the step-completed event with the answers
   * (the funnel needs to see WHICH shape of learner drops here), then routes:
   * a Michigan limited-license learner or someone who already passed has nothing
   * to schedule, so the exam-date screen is skipped with the implied entry
   * recorded rather than shown.
   */
  const completeLicensing = useCallback(() => {
    logAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, {
      step: 'licensing_pathway',
      step_index: screenOrder.indexOf('licensing'),
      exam_type: exam,
      jurisdiction: licensingAnswers.jurisdiction,
      rung: licensingAnswers.licenseRung,
      stage: licensingAnswers.processStage,
      employment: licensingAnswers.employment,
      exam_required: isAvailable(licensing.path) ? licensing.path.examRequiredFirst : undefined,
      timestamp: new Date().toISOString(),
    });
    if (foldedSchedule.skipExamScreen) {
      setScheduleEntry(foldedSchedule.entry);
      goTo('goal');
      return;
    }
    goTo('exam');
  }, [exam, foldedSchedule, goTo, licensing.path, licensingAnswers, screenOrder]);

  // A scheduled exam date is the only ExamScheduleEntry variant createTrialUser
  // can take (it wants YYYY-MM-DD, future). Guard it to strictly-future so a
  // same-day/past pick can never make the backend reject the trial grant — the
  // full entry (any status) still lands via the updateUserProfile write below.
  // Built once per render from the optional step. Null (skipped / "No") means the
  // key never enters the createTrialUser payload, so a first-timer is never
  // stamped isRetaker.
  const retakeReport = buildRetakeReport(retakeAnswers, track);

  const scheduledExamDate =
    scheduleEntry?.status === 'scheduled' &&
    scheduleEntry.date &&
    new Date(scheduleEntry.date) > new Date()
      ? scheduleEntry.date
      : undefined;

  // Best-effort persist of the fields createTrialUser does NOT accept
  // (studyGoal, preferredStudyTime, the full schedule entry) + onboardingCompleted.
  // Uses the SAME callable/shape Onboarding.tsx uses. NEVER throws — a failure
  // must not block the already-successful signup.
  const persistPersonalization = useCallback(async (retake?: RetakeReport | null): Promise<boolean> => {
    const entry: ExamScheduleEntry = scheduleEntry ?? { status: 'exploring', updatedAt: '' };
    try {
      await profileService.updateProfile({
        name: name.trim(),
        studyGoal: DEFAULT_STUDY_GOAL,
        preferredStudyTime: preferredStudyTime ?? PreferredStudyTime.MORNING,
        onboardingCompleted: true,
        targetExam: track,
        examScheduleForTestType: { testType: track, entry },
        // The jurisdiction answers ride the same best-effort write (#411),
        // through the ONE payload builder both writers use, so an unanswered
        // question never reaches the callable as an empty string. They also ride
        // createTrialUser, so a failure here loses nothing already stored at
        // account creation.
        ...licensingPayload(licensingAnswers),
        ...(retake ? { retakeReport: retake } : {}),
      });
      return true;
    } catch (err) {
      logger.warn(
        'OnboardingPage',
        'Best-effort personalization write failed — proceeding to dashboard',
        { err }
      );
      return false;
    }
  }, [name, preferredStudyTime, scheduleEntry, track, licensingAnswers]);

  const handleTrialError = (message: string) => {
    setError(message);
  };

  // Retakers default to "pass this time"; a first-timer defaults to "pass the
  // first time". Either is a valid answer on its own, so a doorway visitor who
  // taps straight through Continue writes the sensible default, never nothing.
  const isRetaking = retakeAnswers.hasRetaken;
  const passGoalValue: 'first' | 'retake' = passGoalOverride ?? (isRetaking ? 'retake' : 'first');
  const studyGoalText = passGoalValue === 'retake' ? 'Pass this time' : DEFAULT_STUDY_GOAL;

  /**
   * Doorway finish (#225): there is no account screen to land on — the visitor
   * is already trial-provisioned from the diagnostic capture — so this writes
   * the gathered profile in one best-effort call, warms getTrialPlan so the
   * plan exists the moment the dashboard mounts, and routes there. Mirrors
   * persistPersonalization's never-block-navigation contract.
   */
  const finishDoorway = useCallback(async () => {
    logAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, {
      src: DOORWAY_SRC,
      exam_type: exam,
      timestamp: new Date().toISOString(),
    });
    setError('');
    setFinalizing(true);
    const entry: ExamScheduleEntry = scheduleEntry ?? { status: 'exploring', updatedAt: '' };
    try {
      await profileService.updateProfile({
        name: name.trim() || undefined,
        studyGoal: studyGoalText,
        preferredStudyTime: preferredStudyTime ?? PreferredStudyTime.MORNING,
        onboardingCompleted: true,
        targetExam: track,
        examScheduleForTestType: { testType: track, entry },
        examDateStatus: doorwayNoDate ? 'none' : undefined,
        studyHour: STUDY_HOUR_BY_TIME[preferredStudyTime ?? PreferredStudyTime.MORNING],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        schoolCode,
        // The doorway skips account creation entirely, so a retake report never
        // rides createTrialUser here — this write is the only chance to land it.
        retakeReport: retakeReport ?? undefined,
        ...licensingPayload(licensingAnswers),
      });
    } catch (err) {
      // NEVER navigate on a failed write. A trial dashboard gate that reads
      // onboardingCompleted (meta #227) sends a false reading straight back to
      // /onboarding — silently swallowing this and routing to /dashboard
      // anyway would loop the visitor forever with nothing to break it. Stay
      // on this screen with an inline error; the Finish button becomes Retry.
      logger.warn('OnboardingPage', 'Doorway finish: profile write failed', { err });
      setFinalizing(false);
      setError("We couldn't save your plan. Check your connection and try again.");
      return;
    }
    try {
      await callWithTimeout(() => httpsCallable(functions, 'getTrialPlan')({}), {
        timeoutMs: 8000,
        retries: 0,
      });
    } catch (err) {
      logger.warn('OnboardingPage', 'Doorway finish: getTrialPlan warm-up failed (non-fatal)', { err });
    }
    skipExamScheduledCelebrationFromOnboarding(scheduledExamDate);
    navigate('/dashboard');
  }, [
    exam,
    scheduleEntry,
    name,
    studyGoalText,
    preferredStudyTime,
    track,
    doorwayNoDate,
    schoolCode,
    retakeReport,
    licensingAnswers,
    scheduledExamDate,
    navigate,
  ]);

  /**
   * The doorway's finish reaches this only when `currentUser` is null — a
   * doorway visitor should already be authenticated (minted at diagnostic
   * capture), but a stale link or a redeem that silently failed can land one
   * here with no session. Rather than lose everything they just answered,
   * fall through to the account screen; `handleTrialSuccess` below runs the
   * SAME `finishDoorway` once that account exists.
   */
  const goToDoorwayAccount = useCallback(() => {
    doorwayNeedsAccountRef.current = true;
    personalizedRef.current = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('exam', exam.toLowerCase());
      window.history.replaceState(window.history.state, '', url.toString());
    } catch {
      /* replaceState best-effort; TrialSignupFlow falls back to diagnostic memory */
    }
    setError('');
    setScreen('account');
  }, [exam]);

  /** The doorway's terminal action: finish if authenticated, else go create the account first. */
  const finishOrCreateAccount = useCallback(() => {
    if (!currentUser) {
      goToDoorwayAccount();
      return;
    }
    void finishDoorway();
  }, [currentUser, goToDoorwayAccount, finishDoorway]);

  /**
   * The aim write carries the fields the Dashboard onboarding check reads
   * (onboardingCompleted, name, studyGoal), and it lands BEFORE the first
   * getTrialPlan: the backend builds and freezes day 1 from targetExam on that read.
   */
  const finishAim = useCallback(
    async (aim: AimAnswers) => {
      const aimTrack = EXAM_TO_TEST_TYPE[aim.exam];
      setError('');
      setAimSaving(true);
      try {
        await saveAimProfile(aim, currentUser, userData?.profile);
      } catch (err) {
        logger.warn('OnboardingPage', 'Aim finish: profile write failed', { err });
        setAimSaving(false);
        setError("We couldn't save your answers. Check your connection and try again.");
        return;
      }
      trackAimDone(true, aim.exam, !!aim.date);
      await warmTrialPlan();
      skipExamScheduledCelebrationFromOnboarding(aim.date ?? undefined);
      // She is signed in with the trial running; the dashboard hop is where the
      // emailed-link cohort stops, so the flag sends her straight into a session.
      navigate(isTryFirstQuestionEnabled() ? `/start?type=${aimTrack}&questions=5&src=aim_first_question` : '/dashboard');
      void refreshUserData?.();
    },
    [userData, currentUser, navigate, refreshUserData]
  );

  const handleTrialSuccess = useCallback(async () => {
    // The mint has served its purpose: they now have a trial, so a later visit
    // should be swept off this page like any other returning user.
    clearPassPlanMint();
    // The doorway's account-creation fallback: run the doorway finish (profile
    // write + getTrialPlan warm-up + /dashboard), never the legacy shortcuts
    // below — those would drop everything the doorway gathered.
    if (doorwayNeedsAccountRef.current) {
      await finishDoorway();
      return;
    }
    // Redirect-completion start (no personalize walked): keep P1 behavior and let
    // the Dashboard soft-onboarding modal pick up personalization.
    if (!personalizedRef.current) {
      navigate('/trial-setup');
      return;
    }
    // Walked the personalize screens → write the gathered profile (best-effort),
    // then land the onboarded trial user directly on the dashboard. onboardingCompleted
    // is now true, so the "Set Your Exam Schedule" modal won't re-prompt.
    setFinalizing(true);
    await persistPersonalization();
    skipExamScheduledCelebrationFromOnboarding(scheduledExamDate);
    navigate('/dashboard');
  }, [navigate, persistPersonalization, finishDoorway, scheduledExamDate]);

  // Same no-navigate-on-failed-write rule as finishDoorway: a missing
  // onboardingCompleted would bounce the learner straight back here.
  const finishMintedTrial = useCallback(async () => {
    logAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, {
      src: 'pass_plan_trial_at_mint',
      exam_type: exam,
      timestamp: new Date().toISOString(),
    });
    setError('');
    setFinalizing(true);
    if (!(await persistPersonalization(retakeReport))) {
      setFinalizing(false);
      setError("We couldn't save your plan. Check your connection and try again.");
      return;
    }
    clearPassPlanMint();
    skipExamScheduledCelebrationFromOnboarding(scheduledExamDate);
    navigate(isTryFirstQuestionEnabled() ? `/start?type=${track}&questions=5&src=aim_first_question` : `/start?type=${track}`);
  }, [exam, track, persistPersonalization, retakeReport, scheduledExamDate, navigate]);

  const stepIndex = screenOrder.indexOf(screen);

  const momentumHeadlineNode =
    readiness !== null ? (
      <>
        You scored <CountUp target={readiness} />%. Here&apos;s the plan that gets you to pass.
      </>
    ) : (
      <>Let&apos;s build the plan that gets you to pass.</>
    );

  // Per-screen "can advance" gate — every personalize screen is REQUIRED,
  // except the doorway's licensing step, which stays optional per spec.
  const canContinue =
    screen === 'licensing'
      ? isDoorway || (!licensing.isLoading && isLicensingComplete(licensingAnswers, licensing.path))
      : screen === 'exam'
      ? isScheduleComplete(scheduleEntry)
      : screen === 'examDate'
      ? isScheduleComplete(scheduleEntry) || doorwayNoDate
      : screen === 'goal'
      ? preferredStudyTime !== null
      : screen === 'name'
      ? name.trim().length > 0
      : true;

  const cardStyle = { boxShadow: '0 6px 0 rgba(17,17,17,0.06)' } as const;

  const BackButton = (
    <button
      type="button"
      onClick={goBack}
      data-testid="onboarding-back"
      className="inline-flex min-h-[44px] items-center gap-1.5 pr-3 text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors mb-2"
    >
      <ArrowLeft className="w-4 h-4" />
      <span>Back</span>
    </button>
  );

  const ContinueButton = (next: OnboardingScreen, testId: string, label = 'Continue') => (
    <StickyAction>
    <button
      type="button"
      data-testid={testId}
      onClick={() => goTo(next)}
      disabled={!canContinue}
      className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ boxShadow: '0 4px 0 #1B3A38' }}
    >
      <span>{label}</span>
      <ArrowRight className="w-5 h-5" />
    </button>
    </StickyAction>
  );

  if (redeeming || (aimSignedIn && authLoading) || (aimEligible && !aimSuppressed && isAuthLoading)) return <BrandedLoader variant="dashboard" />;
  if (showAim) {
    return (
      <AimScreen
        exams={EXAM_CHOICES}
        initialExam={exam}
        initialRetake={learnerContext().retake ?? false}
        submitting={aimSaving}
        error={error}
        onSubmit={(aim) => void finishAim(aim)}
      />
    );
  }

  return (
    <ErrorBoundary>
      <Helmet>
        <title>Build Your Study Plan - Social Work Test Prep Academy</title>
        <meta
          name="description"
          content="Build your personalized ASWB study plan and get 7 free days of everything. Adaptive practice for the LCSW, LMSW, and LBSW exams. No card, no billing, nothing to cancel."
        />
        <meta property="og:title" content="Build Your Study Plan - SWTPA" />
        <meta property="og:description" content="Your personalized ASWB study plan, then 7 free days of everything. No card, no billing, nothing to cancel." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socialworktestprepacademy.com/onboarding" />
        <link rel="canonical" href="https://www.socialworktestprepacademy.com/onboarding" />
      </Helmet>

      {finalizing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-50 px-4"
          role="status"
          aria-live="polite"
          data-testid="onboarding-finalizing"
        >
          <div
            className="w-full max-w-md rounded-2xl border-2 border-transparent p-8 text-center text-white"
            style={{ backgroundColor: '#2C5F5D', boxShadow: '0 6px 0 rgba(17,17,17,0.12)' }}
          >
            <div
              className="mx-auto mb-5 h-10 w-10 rounded-full border-[3px] border-white/30 border-t-white motion-safe:animate-spin"
              aria-hidden="true"
            />
            <p className="text-xl font-display font-bold">Building your study plan…</p>
            <p className="mt-2 text-sm text-white/80">Locking in your {exam} plan and setting up your free account.</p>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-neutral-50 py-8 sm:py-12 px-4">
        <div className="w-full max-w-md mx-auto">
          {/* Progress rail — the full sequence: plan → exam → goal → name → account. */}
          <div className="flex items-center gap-2 mb-6" aria-hidden="true">
            {screenOrder.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                  i <= stepIndex ? 'bg-primary-500' : 'bg-neutral-200'
                }`}
              />
            ))}
          </div>

          {error && (
            <div className="mb-4 p-4 rounded-xl bg-error-light border border-error/30">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* ── Screen A — plan + exam confirm ─────────────────────────────── */}
          {screen === 'plan' && (
            <div className="animate-fade-in" data-testid="onboarding-plan">
              {/* Momentum header — teal, white text, carries the diagnostic forward.
                  Count & Build: the score counts up here, then the blocks below
                  stagger in (motion-safe; reduced-motion shows everything at rest). */}
              <div
                className="rounded-2xl border-2 border-transparent p-6 md:p-7 mb-5 text-white motion-safe:animate-fade-in-up"
                style={{ backgroundColor: '#2C5F5D', boxShadow: '0 6px 0 rgba(17,17,17,0.12)', animationDelay: '40ms', animationFillMode: 'both' }}
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-2">
                  Your study plan
                </p>
                <h1 className="text-2xl md:text-3xl font-display font-bold leading-tight text-white">
                  {momentumHeadlineNode}
                </h1>
                <p className="text-white/80 mt-3 text-sm md:text-base">
                  Adaptive practice that keeps finding your weak spots and drills them until
                  they stick, every question reviewed by Pam Coin, LCSW.
                </p>
              </div>

              {/* Exam confirm */}
              {askExam && (
                <div
                  className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5 motion-safe:animate-fade-in-up"
                  style={{ ...cardStyle, animationDelay: '520ms', animationFillMode: 'both' }}
                >
                  <p className="text-base font-display font-bold text-neutral-900 mb-1">
                    Which exam are you preparing for?
                  </p>
                  <p className="text-sm text-neutral-500 mb-4">
                    We tailor your plan and questions to this exam.
                  </p>
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Exam type">
                    {EXAM_CHOICES.map((choice) => {
                      const selected = exam === choice;
                      return (
                        <button
                          key={choice}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          data-testid={`onboarding-exam-${choice}`}
                          onClick={() => setExam(choice)}
                          className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-colors ${
                            selected
                              ? 'bg-primary-600 text-white border-primary-700'
                              : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary-300'
                          }`}
                        >
                          {choice}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div
                className="motion-safe:animate-fade-in-up"
                style={{ animationDelay: '680ms', animationFillMode: 'both' }}
              >
                {ContinueButton(
                  isDoorway ? 'examDate' : licensingEnabled ? 'licensing' : 'exam',
                  'onboarding-to-exam',
                  'Build my study plan'
                )}

                {/* A doorway visitor already has an account — nothing to sign into. */}
                {!isDoorway && (
                  <p className="mt-6 text-center text-sm text-neutral-500">
                    Already have an account?{' '}
                    <Link to="/signin" className="font-medium text-primary-600 hover:underline">
                      Sign in
                    </Link>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Screen A2 — where are you getting licensed? (#411, flagged) ── */}
          {screen === 'licensing' && (
            <div className="animate-fade-in" data-testid="onboarding-licensing">
              {BackButton}
              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                <LicensingStep
                  answers={licensingAnswers}
                  onChange={setLicensingAnswers}
                  path={licensing.path}
                  isLoading={licensing.isLoading}
                  hasError={licensing.hasError}
                />
              </div>
              <StickyAction>
              <button
                type="button"
                data-testid="onboarding-to-schedule"
                onClick={isDoorway ? () => goTo('goal') : completeLicensing}
                disabled={!canContinue}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ boxShadow: '0 4px 0 #1B3A38' }}
              >
                <span>Continue</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              </StickyAction>
            </div>
          )}

          {/* ── Doorway — exam date, with a real "no date yet" (#225) ───────── */}
          {screen === 'examDate' && (
            <div className="animate-fade-in" data-testid="onboarding-examdate">
              {BackButton}
              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                <h2 className="text-xl font-display font-bold text-neutral-900 mb-1">
                  When are you testing?
                </h2>
                <p className="text-sm text-neutral-500 mb-4">
                  Pick a date if you have one. If you don&apos;t yet, say so. We never guess one for you.
                </p>
                <input
                  type="date"
                  value={scheduleEntry?.status === 'scheduled' ? scheduleEntry.date ?? '' : ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    setDoorwayNoDate(false);
                    setScheduleEntry(date ? { status: 'scheduled', date, updatedAt: new Date().toISOString() } : null);
                  }}
                  data-testid="onboarding-examdate-input"
                  className="w-full px-4 py-3.5 text-lg border-2 border-neutral-200 rounded-xl bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all mb-3"
                />
                <button
                  type="button"
                  data-testid="onboarding-examdate-none"
                  aria-pressed={doorwayNoDate}
                  onClick={() => {
                    setDoorwayNoDate(true);
                    setScheduleEntry(null);
                  }}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    doorwayNoDate
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-neutral-200 bg-neutral-100 text-neutral-900 hover:border-neutral-300'
                  }`}
                >
                  <span className="font-semibold">I don&apos;t have a date yet</span>
                </button>
              </div>
              {ContinueButton('retake', 'onboarding-to-retake-from-examdate')}
            </div>
          )}

          {/* ── Screen B — exam date / status (reused ExamScheduler) ────────── */}
          {screen === 'exam' && (
            <div className="animate-fade-in" data-testid="onboarding-exam">
              {BackButton}
              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                <h2 className="text-xl font-display font-bold text-neutral-900 mb-1">
                  When are you testing?
                </h2>
                <p className="text-sm text-neutral-500 mb-4">
                  This shapes the pace of your plan. Pick whatever's true today.
                </p>
                <ExamScheduler
                  track={track}
                  persist={false}
                  initialEntry={scheduleEntry}
                  initialStatus={urlExamStatus ?? foldedSchedule.status}
                  onChange={setScheduleEntry}
                />
              </div>
              {ContinueButton('goal', 'onboarding-to-goal')}
            </div>
          )}

          {/* ── Screen C — study goal (preferred study time) ───────────────── */}
          {screen === 'goal' && (
            <div className="animate-fade-in" data-testid="onboarding-goal">
              {BackButton}
              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                <h2 className="text-xl font-display font-bold text-neutral-900 mb-1">
                  When do you study best?
                </h2>
                <p className="text-sm text-neutral-500 mb-4">
                  We'll nudge your practice toward the time you're sharpest.
                </p>
                <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Preferred study time">
                  {STUDY_TIME_OPTIONS.map((option) => {
                    const selected = preferredStudyTime === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        data-testid={`onboarding-studytime-${option.value}`}
                        onClick={() => setPreferredStudyTime(option.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-neutral-200 bg-neutral-100 hover:border-neutral-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-semibold ${selected ? 'text-primary-700' : 'text-neutral-900'}`}>
                            {option.label}
                          </span>
                          {selected && (
                            <span className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-neutral-500">{option.time}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Doorway only: the school lookup rides this screen as an inline,
                  collapsed-by-default link so it never becomes a seventh tap. */}
              {isDoorway && (
                <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                  {!schoolExpanded ? (
                    <button
                      type="button"
                      data-testid="onboarding-add-program"
                      onClick={() => setSchoolExpanded(true)}
                      className="text-sm font-semibold text-primary-700 underline"
                    >
                      + Add my program
                    </button>
                  ) : (
                    <>
                      <p className="text-sm font-bold text-neutral-900 mb-1">Which program are you in?</p>
                      <p className="text-sm text-neutral-500 mb-3">
                        Optional. Shows you how your program&apos;s grads do on this exam.
                      </p>
                      <input
                        type="text"
                        value={schoolQuery}
                        onChange={(e) => {
                          setSchoolQuery(e.target.value);
                          setSchoolCode(undefined);
                        }}
                        placeholder="Search your school"
                        autoComplete="off"
                        autoFocus
                        data-testid="onboarding-school-input"
                        className="w-full px-4 py-3.5 text-lg border-2 border-neutral-200 rounded-xl bg-neutral-100 text-neutral-900 placeholder-neutral-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all mb-3"
                      />
                      {schoolQuery.trim().length > 1 && !schoolCode && (
                        <div className="max-h-56 overflow-y-auto rounded-xl border-2 border-neutral-200 divide-y divide-neutral-100">
                          {filterSchools(allSchools(), schoolQuery)
                            .slice(0, 8)
                            .map((school) => (
                              <button
                                key={school.schoolCode}
                                type="button"
                                data-testid={`onboarding-school-${school.schoolCode}`}
                                onClick={() => {
                                  setSchoolCode(school.schoolCode);
                                  setSchoolQuery(school.name);
                                }}
                                className="w-full text-left px-4 py-3 text-neutral-800 hover:bg-primary-50"
                              >
                                {school.name}
                              </button>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {isDoorway ? (
                <StickyAction>
                <button
                  type="button"
                  data-testid="onboarding-finish"
                  onClick={finishOrCreateAccount}
                  disabled={!canContinue}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ boxShadow: '0 4px 0 #1B3A38' }}
                >
                  <span>{error ? 'Retry' : 'Finish'}</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                </StickyAction>
              ) : (
                ContinueButton('name', 'onboarding-to-name')
              )}
            </div>
          )}

          {/* ── Screen D — name ────────────────────────────────────────────── */}
          {screen === 'name' && (
            <div className="animate-fade-in" data-testid="onboarding-name">
              {BackButton}
              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                <h2 className="text-xl font-display font-bold text-neutral-900 mb-1">
                  What should we call you?
                </h2>
                <p className="text-sm text-neutral-500 mb-4">
                  Your first name personalizes the app and your study emails.
                </p>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your first name"
                  autoComplete="given-name"
                  autoFocus
                  data-testid="onboarding-name-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && name.trim().length > 0) goTo('retake');
                  }}
                  className="w-full px-4 py-3.5 text-lg border-2 border-neutral-200 rounded-xl bg-neutral-100 text-neutral-900 placeholder-neutral-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                />
              </div>
              {ContinueButton('retake', 'onboarding-to-retake')}
            </div>
          )}

          {/* ── Screen D2 — optional prior-attempt capture ──────────────────── */}
          {screen === 'retake' && (
            <div className="animate-fade-in" data-testid="onboarding-retake">
              {BackButton}
              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                <RetakeStep exam={track} value={retakeAnswers} onChange={setRetakeAnswers} />
              </div>
              {isDoorway ? (
                ContinueButton('passGoal', 'onboarding-to-passgoal')
              ) : trialAtMint ? (
                <StickyAction>
                <button
                  type="button"
                  data-testid="onboarding-finish-minted"
                  onClick={() => void finishMintedTrial()}
                  disabled={finalizing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ boxShadow: '0 4px 0 #1B3A38' }}
                >
                  <span>{error ? 'Retry' : 'Start my first question'}</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                </StickyAction>
              ) : (
                ContinueButton('account', 'onboarding-to-account', 'Create my account')
              )}
            </div>
          )}

          {/* ── Doorway — goal: pass the first time, or pass this time (#225) ── */}
          {screen === 'passGoal' && (
            <div className="animate-fade-in" data-testid="onboarding-passgoal">
              {BackButton}
              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 mb-5" style={cardStyle}>
                <h2 className="text-xl font-display font-bold text-neutral-900 mb-1">What&apos;s the goal?</h2>
                <p className="text-sm text-neutral-500 mb-4">Shapes how we frame your plan, nothing more.</p>
                <div className="grid gap-3" role="radiogroup" aria-label="Study goal">
                  {(['first', 'retake'] as const).map((value) => {
                    const label = value === 'first' ? 'Pass the first time' : 'Pass this time';
                    const selected = passGoalValue === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        data-testid={`onboarding-passgoal-${value}`}
                        onClick={() => setPassGoalOverride(value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selected
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-neutral-200 bg-neutral-100 text-neutral-900 hover:border-neutral-300'
                        }`}
                      >
                        <span className="font-semibold">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {ContinueButton('licensing', 'onboarding-to-licensing-from-passgoal')}
            </div>
          )}

          {/* ── Screen E — account (existing TrialSignupFlow, LAST step) ────── */}
          {screen === 'account' && (
            <div className="animate-fade-in" data-testid="onboarding-account">
              {BackButton}

              <div className="bg-neutral-100 rounded-2xl shadow-ledge-lg border border-neutral-200 overflow-hidden">
                <div className="px-6 pt-6 pb-4 border-b border-neutral-200">
                  <p className="text-sm text-primary-600 font-medium mb-1">Last step</p>
                  <h2 className="text-xl font-semibold text-neutral-900 font-display">
                    {mintedContinuer ? 'Start your free trial' : 'Create your account'}
                  </h2>
                  <p className="text-sm text-neutral-500 mt-1">
                    Save your plan and get 7 free days of everything. No card, no billing, nothing to cancel.
                  </p>
                </div>
                <div className="p-6">
                  {/* Auth/provisioning UNCHANGED. `personalization` is additive: name +
                      a scheduled exam date ride into the createTrialUser payload. */}
                  <TrialSignupFlow
                    onSuccess={handleTrialSuccess}
                    onError={handleTrialError}
                    personalization={{
                      name: name.trim() || undefined,
                      examDate: scheduledExamDate,
                      retakeReport: retakeReport ?? undefined,
                      isRetaker: retakeReport ? true : undefined,
                      // Answered fields only; an untouched step sends nothing.
                      licensing: licensingEnabled ? licensingAnswers : undefined,
                    }}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs text-neutral-500">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-success" />
                  <span>SSL Secured</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-primary-500" />
                  <span>No card, no billing</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default OnboardingPage;
